import type { Product } from '@equinox/shared';

/**
 * One option (flavour) row in the product form. Pure data, no React, so the
 * rules (what is savable, what the server is sent, what the product price
 * becomes) are unit-testable without rendering the form.
 */
export interface OptionDraft {
  /** Stable React key; never sent. */
  key: string;
  /** Present for an option that already exists; absent for a new one. */
  id?: string;
  name: string;
  priceCents: number | null;
  priceValid: boolean;
  costCents: number | null;
  costValid: boolean;
  stockQty: number | null;
  stockValid: boolean;
  /** Stock the option had when the form opened (0 for a new one). */
  originalStock: number;
  active: boolean;
  /** An extra (a cheese): priced ON TOP of the flavour, optional, never a flavour, no stock or cost. */
  isAddon: boolean;
  /** The picker column it is shown under; blank = the product's own list. */
  groupName: string;
}

let counter = 0;
export function newOptionKey(): string {
  counter += 1;
  return `opt-${counter}`;
}

export function blankOption(): OptionDraft {
  return {
    key: newOptionKey(),
    name: '',
    priceCents: null,
    priceValid: true,
    costCents: null,
    costValid: true,
    stockQty: 0,
    stockValid: true,
    originalStock: 0,
    active: true,
    isAddon: false,
    groupName: '',
  };
}

export function draftsFromProduct(product: Pick<Product, 'options'> | null): OptionDraft[] {
  return [...(product?.options ?? [])]
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
    .map((o) => ({
      key: newOptionKey(),
      id: o.id,
      name: o.name,
      priceCents: o.priceCents,
      priceValid: true,
      costCents: o.costCents ?? null,
      costValid: true,
      stockQty: o.stockQty,
      stockValid: true,
      originalStock: o.stockQty,
      active: o.active,
      isAddon: o.isAddon === true,
      groupName: o.groupName ?? '',
    }));
}

export type OptionsProblem = 'none' | 'nameRequired' | 'priceRequired' | 'duplicateName' | 'invalidNumber' | 'noneActive';

/** The first thing that stops the options being saved, as a kind (the caller translates it). */
export function optionsProblem(drafts: OptionDraft[]): OptionsProblem | null {
  if (drafts.length === 0) return 'none';
  const seen = new Set<string>();
  for (const d of drafts) {
    if (!d.name.trim()) return 'nameRequired';
    if (d.priceCents == null) return 'priceRequired';
    if (!d.priceValid || !d.costValid || !d.stockValid) return 'invalidNumber';
    const k = d.name.trim().toLowerCase();
    if (seen.has(k)) return 'duplicateName';
    seen.add(k);
  }
  // Options on, no active FLAVOUR (extras alone sell nothing and set no price).
  if (!drafts.some((d) => d.active && !d.isAddon)) return 'noneActive';
  return null;
}

/** What the product's own price becomes: the lowest ACTIVE option price. */
export function lowestActivePrice(drafts: OptionDraft[]): number | null {
  // Flavours only: an extra priced 0 must never drag the product to $0.
  const prices = drafts
    .filter((d) => d.active && !d.isAddon && d.priceCents != null)
    .map((d) => d.priceCents as number);
  return prices.length > 0 ? Math.min(...prices) : null;
}

/** The request body's `options`: the full desired set, in the order shown. */
export function toOptionInputs(drafts: OptionDraft[]) {
  return drafts.map((d, index) => ({
    ...(d.id ? { id: d.id } : {}),
    name: d.name.trim(),
    priceCents: d.priceCents as number,
    costCents: d.isAddon ? null : d.costCents,
    sortOrder: index,
    active: d.active,
    isAddon: d.isAddon,
    groupName: d.groupName.trim() || null,
  }));
}

/**
 * Stock goes through its own endpoint (never the generic update). Returns the
 * option ids whose stock must be set, with the value, matching saved options
 * by id, or by name for options that were just created.
 */
/**
 * The drafts after a save reached the server: every draft takes the id of its
 * saved option (matched by id, else by name) and its stock baseline from what
 * the server holds now. An option the admin never touched takes the server's
 * count for both values (so it is never rewritten); one they changed keeps what
 * they typed. This is what makes a retry after a half-failed save update
 * instead of creating again, without ever writing back a stale count.
 */
export function reconcileDrafts(
  drafts: OptionDraft[],
  saved: ReadonlyArray<{ id: string; name: string; stockQty: number }>,
): OptionDraft[] {
  return drafts.map((d) => {
    const match = d.id
      ? saved.find((s) => s.id === d.id)
      : saved.find((s) => s.name.toLowerCase() === d.name.trim().toLowerCase());
    if (!match) return d;
    // An option whose stock the admin did NOT touch adopts what the server holds
    // now for BOTH the typed value and the baseline: it may have sold while the
    // form was open, and a retry must not write the stale count back over those
    // sales. One the admin DID change keeps what they typed ("set to X"), with
    // the server's count as its new baseline.
    const untouched = d.stockQty === d.originalStock;
    return {
      ...d,
      id: match.id,
      originalStock: match.stockQty,
      stockQty: untouched ? match.stockQty : d.stockQty,
    };
  });
}

export function stockAdjustments(
  drafts: OptionDraft[],
  saved: ReadonlyArray<{ id: string; name: string }>,
): Array<{ optionId: string; stockQty: number }> {
  const out: Array<{ optionId: string; stockQty: number }> = [];
  for (const d of drafts) {
    if (d.isAddon) continue; // extras carry no stock
    const desired = d.stockQty ?? 0;
    if (desired === d.originalStock) continue;
    const target = d.id
      ? saved.find((s) => s.id === d.id)
      : saved.find((s) => s.name.toLowerCase() === d.name.trim().toLowerCase());
    if (target) out.push({ optionId: target.id, stockQty: desired });
  }
  return out;
}
